import React, { useCallback, useEffect, useRef, useState } from "react";

// A small right-click menu, used on the overview cards, the treatment rows and
// the questionnaire sections.
//
//   const { show, menu } = useContextMenu();
//   <div onContextMenu={(e) => show(e, [{ label: "Open", onClick: open }])}>
//   …
//   {menu}
//
// An item is { label, onClick, disabled?, hint? }; { divider: true } draws a rule.
// The menu closes on any click, Escape, scroll or resize, and is kept inside
// the window. Arrow keys and Enter work once it is open.
export function useContextMenu() {
  const [state, setState] = useState(null);

  const close = useCallback(() => setState(null), []);

  const show = useCallback((event, items) => {
    event.preventDefault();
    event.stopPropagation();
    setState({ x: event.clientX, y: event.clientY, items });
  }, []);

  const menu = state ? <ContextMenu {...state} onClose={close} /> : null;
  return { show, menu };
}

function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [active, setActive] = useState(-1);
  const actionable = items.map((it, i) => (it.divider || it.disabled ? -1 : i)).filter((i) => i >= 0);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      const { width, height } = el.getBoundingClientRect();
      setPos({
        left: Math.max(4, Math.min(x, window.innerWidth - width - 4)),
        top: Math.max(4, Math.min(y, window.innerHeight - height - 4)),
      });
      el.focus();
    }
    const away = (e) => { if (!el || !el.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [x, y, onClose]);

  const run = (item) => {
    onClose();
    item.onClick && item.onClick();
  };

  const onKeyDown = (e) => {
    if (!actionable.length) return;
    const at = actionable.indexOf(active);
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(actionable[(at + 1) % actionable.length]); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(actionable[(at - 1 + actionable.length) % actionable.length]); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); run(items[active]); }
  };

  return (
    <ul
      ref={ref}
      className="ox-menu"
      role="menu"
      tabIndex={-1}
      style={{ left: pos.left, top: pos.top }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) =>
        item.divider ? (
          <li key={i} className="ox-menu__divider" role="separator" />
        ) : (
          <li key={i} role="none">
            <button
              type="button"
              role="menuitem"
              className={`ox-menu__item${active === i ? " is-active" : ""}`}
              disabled={item.disabled}
              onMouseEnter={() => setActive(i)}
              onClick={() => run(item)}
            >
              <span>{item.label}</span>
              {item.hint && <small>{item.hint}</small>}
            </button>
          </li>
        ),
      )}
    </ul>
  );
}
